import React, { useMemo, useState } from 'react'
import { ArrowLeft, RotateCw, Target } from 'lucide-react'
import { FERMI, fermiScore } from './data.js'
import { tr, useLang } from '../lib/lang.js'
import { LangToggle } from '../lib/LangToggle.jsx'
import { rngFrom, shuffle } from '../lib/random.js'
import { awardBp } from '../lib/store.js'

const ROUND = 6

function fmt(n) {
  return new Intl.NumberFormat('es').format(n)
}

export function BazarGame({ onBack, onComplete }) {
  const [lang, setLang] = useLang()
  const [roundKey, setRoundKey] = useState(0)
  const [index, setIndex] = useState(0)
  const [guess, setGuess] = useState('')
  const [scored, setScored] = useState(null)
  const [total, setTotal] = useState(0)
  const [done, setDone] = useState(false)

  const deck = useMemo(() => {
    const rng = rngFrom((roundKey + 1) * 55103)
    return shuffle(rng, FERMI).slice(0, ROUND)
  }, [roundKey])

  const current = deck[index]

  const submit = () => {
    if (scored || guess === '') return
    const pts = fermiScore(guess, current.a)
    setScored(pts)
    setTotal((t) => t + pts)
    awardBp(Math.round(pts / 2))
  }

  const next = () => {
    const finalTotal = total
    if (index + 1 >= deck.length) {
      onComplete?.(Math.round(finalTotal / 2))
      setDone(true)
      return
    }
    setIndex((i) => i + 1)
    setGuess('')
    setScored(null)
  }

  const restart = () => {
    setRoundKey((k) => k + 1)
    setIndex(0)
    setGuess('')
    setScored(null)
    setTotal(0)
    setDone(false)
  }

  if (done) {
    const max = deck.length * 100
    return (
      <div className="flex flex-col items-center justify-center min-h-[55vh] text-center space-y-5">
        <Target size={40} className="text-amber-400" />
        <h2 className="text-xl font-black text-white">
          {total}/{max}
        </h2>
        <p className="text-xs text-slate-400">
          {tr(lang, 'Puntos de estimación', 'Estimation points')}
        </p>
        <div className="flex gap-2 w-full max-w-xs">
          <button
            type="button"
            onClick={restart}
            className="flex-1 py-3 rounded-xl bg-amber-500 text-slate-950 font-black text-sm cursor-pointer flex items-center justify-center gap-2"
          >
            <RotateCw size={16} /> {tr(lang, 'Otra vez', 'Again')}
          </button>
          <button
            type="button"
            onClick={onBack}
            className="flex-1 py-3 rounded-xl bg-slate-900 border border-slate-800 text-white font-black text-sm cursor-pointer"
          >
            {tr(lang, 'Salir', 'Exit')}
          </button>
        </div>
      </div>
    )
  }

  const ratio = scored != null ? Math.max(Number(guess) || 1, 1) / current.a : null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white cursor-pointer"
        >
          <ArrowLeft size={16} /> Volver
        </button>
        <LangToggle lang={lang} onChange={setLang} />
      </div>

      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <span className="font-bold uppercase tracking-wider">El Bazar · Fermi</span>
        <span>
          {index + 1}/{deck.length} · {total} pts
        </span>
      </div>

      <div className="p-6 rounded-2xl bg-gradient-to-br from-[#141008] to-[#0c0b07] border border-amber-900/40 text-center">
        <div className="text-4xl mb-2">{current.icon}</div>
        <p className="text-base sm:text-lg font-bold text-white leading-snug">
          {tr(lang, current.q.es, current.q.en)}
        </p>
        <p className="text-[11px] text-slate-500 mt-2">
          {tr(lang, 'No importa la exactitud: importa el orden de magnitud.', 'Exactness isn’t the point — the order of magnitude is.')}
        </p>
      </div>

      {scored == null ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input
              type="number"
              inputMode="numeric"
              value={guess}
              onChange={(e) => setGuess(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              placeholder={tr(lang, 'Tu estimación', 'Your estimate')}
              className="flex-1 px-4 py-3 rounded-xl bg-[#0e131f] border border-slate-800 text-white text-lg font-black outline-none focus:border-amber-500/50"
            />
            <span className="text-xs font-bold text-slate-400 shrink-0">
              {tr(lang, current.unit.es, current.unit.en)}
            </span>
          </div>
          <button
            type="button"
            disabled={guess === ''}
            onClick={submit}
            className="w-full py-3 rounded-xl bg-amber-500 text-slate-950 font-black text-sm disabled:opacity-40 cursor-pointer"
          >
            {tr(lang, 'Apostar', 'Guess')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="p-4 rounded-2xl bg-[#0e131f] border border-slate-800 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">{tr(lang, 'Respuesta', 'Answer')}</span>
              <span className="font-black text-white">
                {fmt(current.a)} {tr(lang, current.unit.es, current.unit.en)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">{tr(lang, 'Tu error', 'Your error')}</span>
              <span className="font-bold text-amber-300">
                {ratio >= 1 ? `×${ratio.toFixed(1)}` : `÷${(1 / ratio).toFixed(1)}`}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">{tr(lang, 'Puntos', 'Points')}</span>
              <span className="font-black text-emerald-300">{scored}/100</span>
            </div>
          </div>
          <button
            type="button"
            onClick={next}
            className="w-full py-3 rounded-xl bg-amber-500 text-slate-950 font-black text-sm cursor-pointer"
          >
            {index + 1 >= deck.length ? tr(lang, 'Ver resultado', 'See result') : tr(lang, 'Siguiente', 'Next')}
          </button>
        </div>
      )}
    </div>
  )
}
